import React, { useEffect, useState } from 'react';
import styles from '../mockInterview.module.scss';
import MockInterviewQuestionList from './MockInterviewQuestionList';
import MockInterviewTurnDetail from './MockInterviewTurnDetail';
import { groupAdvice } from '../../EvaluationDetail/components/adviceLogic';
import { isAttemptInterrupted } from './attemptInterrupted';
import { getQuestionEvaluateStatus, jobsByQuestion, countFailedQuestions, resolveQuestionEvaluate, isAudioLostJob, isSkippedEvaluate } from './evaluateJobState';

// One dialog answer's advice, computed with the exact same rule-matching logic
// as AdviceSection (see adviceLogic.js) - just re-targeted at that single
// turn's own scoring instead of the whole-dialog aggregate. `evaladvicerules`
// keys are written relative to the whole answer object (`evaluation.depth.depth_score`,
// ...), so matchedScoredTurn (shape {depth, relevance, errors, practice, fillers, style})
// is wrapped as { evaluation: matchedScoredTurn } before matching - existing
// rule keys then just work, no path rewriting needed.
function computeTurnAdvice(matchedScoredTurn, rules, schemas) {
    const groups = groupAdvice(rules, schemas, { evaluation: matchedScoredTurn });
    const advice = Object.values(groups).flat().map(rule => rule.advice);
    const criticalErrors = matchedScoredTurn.errors?.is_critical
        ? (matchedScoredTurn.errors.errors || [])
        : [];
    return [...criticalErrors, ...advice];
}

// Attaches `advice` (flat string[]) to every dialog entry, matched to its
// per-turn scoring by turn_id (not array position - meta-turns like "Начнём."
// are filtered out before scoring, so `scoredTurns` can be shorter than
// `dialog`). Entries with no match (meta-turns, or scoring not done yet)
// get advice: [] - MockInterviewDialogChat's badge just doesn't render for those.
function withDialogAdvice(dialog, scoredTurns, rules, schemas) {
    if (!Array.isArray(dialog)) return dialog;
    return dialog.map(entry => {
        const matchedScoredTurn = (scoredTurns || []).find(t => t.turn_id === entry.turn_id);
        return {
            ...entry,
            advice: matchedScoredTurn ? computeTurnAdvice(matchedScoredTurn, rules, schemas) : [],
        };
    });
}

const MockInterviewResults = ({ interview, onRefresh }) => {
    const rawTurns = interview.turns || [];
    const evaluateByQuestion = {};
    (interview.evaluate || []).forEach(e => {
        evaluateByQuestion[e.questionId] = e.evaluate;
    });
    const jobs = jobsByQuestion(interview.evaluateState);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [retryingQuestion, setRetryingQuestion] = useState(null);
    const [adviceRules, setAdviceRules] = useState([]);
    const [metricSchemas, setMetricSchemas] = useState([]);

    const turns = rawTurns.map(turn => {
        const job = jobs[turn.question_id];
        const evaluate = resolveQuestionEvaluate(evaluateByQuestion[turn.question_id], job);
        return {
            ...turn,
            evaluate,
            evaluateStatus: getQuestionEvaluateStatus(evaluate, job),
            evaluateId: job?.evaluateId ?? null,
            evaluateExplain: job?.explain ?? null,
            audioLost: isAudioLostJob(job),
            skipped: isSkippedEvaluate(evaluate),
            dialog: turn.dialog
                ? withDialogAdvice(turn.dialog, evaluate?.turns, adviceRules, metricSchemas)
                : turn.dialog,
        };
    });

    // Точечный перезапуск оценки одного вопроса: остальные вопросы уже оценены,
    // гонять всю пачку заново незачем. Ответ пользователя на бэкенде сохранён,
    // перезапуск переоценивает именно его.
    // Когда аудио ответа потеряно (audioLost), гонять оценку с записью бессмысленно -
    // просим сервис оценки посчитать балл по одному тексту (mode: 'text'), без
    // аудио-метрик. Готовая оценка приходит помеченной как textOnly.
    const retryQuestion = (questionId, { textOnly } = {}) => {
        setRetryingQuestion(questionId);
        const payload = textOnly ? { questionId, mode: 'text' } : { questionId };
        return global.http.post(`/mock-interview/${interview._id}/evaluate-retry`, payload, { wo_notify: true })
            .then(() => {
                global.notify.success('Оценка запущена. Результат появится через минуту.');
                onRefresh && onRefresh();
            })
            .catch(() => {
                global.notify.warning('Оценка снова не запустилась. Попробуйте позже.');
            })
            .finally(() => setRetryingQuestion(null));
    };

    useEffect(() => {
        global.http.get('/eval-advice-rule', { per_page: 200 }).then(r => setAdviceRules(r.items || []));
        global.http.get('/eval-metric-schemas').then(r => setMetricSchemas(r.items || []));
    }, []);

    // Прерванная попытка: кандидат вышел, не дойдя до последнего вопроса
    // (см. attemptInterrupted.js). Без этой строки короткое интервью на два
    // вопроса выглядит на экране так же, как честно пройденное, и низкий балл
    // читается как оценка знаний, а не как оборванный разговор.
    const interrupted = isAttemptInterrupted(interview);
    const interruptedNote = interrupted && (
        <div className={styles.interruptedNote} data-testid="mock-interview-interrupted-note">
            <i className="iconoir-warning-triangle"/>
            <div>
                <span className={styles.interruptedTitle}>Интервью прервано</span>
                {turns.length
                    ? ' Вы вышли до последнего вопроса — ниже разобраны только те ответы, что успели прозвучать.'
                    : ' Вы вышли, не ответив ни на один вопрос, поэтому разбирать нечего. Пройдите интервью заново, когда будет время на весь разговор.'}
            </div>
        </div>
    );

    // Пустой попытке отдельная карточка-заглушка не нужна: она занимала целый
    // экран ради одной серой строки и висела в отрыве от того, к чему относится.
    // Про отсутствие результатов теперь пишет история попыток - прямо у той
    // попытки, у которой их нет (см. MockInterviewAttemptHistory).
    if (!turns.length) {
        return interruptedNote || null;
    }

    return (
        <>
        {interruptedNote}
        <div className={`mainCont2 row`}>
            <div className="col-sm-3 sticky3">
                <MockInterviewQuestionList
                    turns={turns}
                    failedCount={countFailedQuestions(turns)}
                    selectedIndex={selectedIndex}
                    onSelect={setSelectedIndex}
                />
            </div>
            <div className="col-sm-9 sticky3">
                <MockInterviewTurnDetail
                    turn={turns[selectedIndex]}
                    adviceRules={adviceRules}
                    metricSchemas={metricSchemas}
                    interviewId={interview._id}
                    onRetryEvaluate={retryQuestion}
                    retrying={retryingQuestion === turns[selectedIndex]?.question_id}
                />
            </div>
        </div>
        </>
    );
};

export default MockInterviewResults;
